import React, { useEffect } from 'react';
import { initMetaPixel, trackPageView } from '../utils/metaPixel';
import { Hero } from '../components/sections/Hero';
import { TextSection } from '../components/sections/TextSection';
import { ChapterList } from '../components/sections/ChapterList';
import { PullQuote } from '../components/sections/PullQuote';
import { FinalCTA } from '../components/sections/FinalCTA';
import { MidCTA } from '../components/sections/MidCTA';
import { Guarantee } from '../components/sections/Guarantee';
import { Divider } from '../components/ui/Divider';
import { StickyCTA } from '../components/ui/StickyCTA';
import { CountdownBanner } from '../components/ui/CountdownBanner';
import { Footer } from '../components/sections/Footer';
export const SalesPage = () => {
  useEffect(() => {
    initMetaPixel();
    trackPageView();
  }, []);
  return <main className="min-h-screen bg-cosmic-gradient bg-fixed text-gray-300 font-body selection:bg-amber-500/30 selection:text-amber-200" data-id="element-201">
      <CountdownBanner data-id="element-202" />
      <Hero data-id="element-203" />

      <TextSection data-id="element-204">
        <p className="first-letter:text-6xl first-letter:font-serif first-letter:text-amber-400 first-letter:float-left first-letter:mr-3 first-letter:mt-2" data-id="element-205">
          Hay una molécula dentro de tu cuerpo ahora mismo.
        </p>
        <p data-id="element-206">
          No es una metáfora. No es un concepto espiritual. Es una molécula real
          — producida por tus propias células, presente en tu sangre, en tus
          pulmones, posiblemente en la glándula más profunda de tu cerebro — que
          tiene la capacidad documentada de disolver tu sentido del yo y
          proyectar tu conciencia hacia experiencias que se sienten más reales
          que cualquier cosa que hayas llamado realidad.
        </p>
        <p data-id="element-207">
          Los científicos la han medido. Los chamanes la han conocido durante
          miles de años. Y sin embargo, casi nadie habla de ella de una manera
          que honre verdaderamente tanto la química como el misterio.
        </p>
        <p className="text-2xl md:text-3xl font-serif text-amber-200 text-center my-12 italic" data-id="element-208">
          Esta molécula se llama DMT.
        </p>
        <p className="text-center font-serif text-xl" data-id="element-209">
          Y puede ser lo más importante que nunca has comprendido completamente
          sobre ti mismo.
        </p>
      </TextSection>

      <Divider data-id="element-210" />

      <TextSection data-id="element-211">
        <h2 className="text-3xl md:text-4xl font-serif text-amber-400 mb-8 text-center" data-id="element-212">
          Algo te ha estado molestando.
        </h2>
        <p data-id="element-213">
          No todo el tiempo. Pero en los momentos silenciosos — cuando el ruido
          se detiene y te quedas solo con la textura de tu propia existencia —
          hay una pregunta que no termina de desaparecer.
        </p>
        <p className="text-2xl font-serif text-center italic my-8" data-id="element-214">
          ¿Es esto todo?
        </p>
        <p data-id="element-215">
          Has leído los libros. Explorado las filosofías. Te has sentado en
          meditación. Quizás has tenido experiencias — destellos — donde los
          bordes de ti mismo parecieron suavizarse, donde algo vasto e
          inexpresable presionó desde adentro de tu pecho, y por un instante
          supiste algo que no pudiste explicar a la mañana siguiente.
        </p>
        <p data-id="element-216">Y entonces la vida te jaló de vuelta. Y el saber se desvaneció.</p>
        <p className="font-serif text-xl text-amber-200" data-id="element-217">
          No lo imaginaste. Pero tampoco pudiste sostenerlo.
        </p>
        <p data-id="element-218">
          Las bibliotecas están llenas de tradiciones espirituales, cada una
          apuntando al mismo horizonte con manos distintas. Los laboratorios
          están llenos de datos sobre la conciencia — fríos, precisos y
          extrañamente incompletos. Internet está llena de reportes de viajes
          que suenan descabellados y testimonios que suenan fabricados.
        </p>
        <p className="text-center font-serif text-2xl mt-12" data-id="element-219">
          Nadie te ha dado el mapa.
        </p>
        <p className="text-center" data-id="element-220">
          No el verdadero. No el que traza una línea directa entre la
          neurociencia y lo numinoso. Entre la molécula y el místico. Entre el
          laboratorio y el altar.
        </p>
        <p className="text-center font-serif text-2xl text-amber-400 mt-8 italic" data-id="element-221">
          Hasta ahora.
        </p>
      </TextSection>

      <Divider data-id="element-222" />

      <TextSection data-id="element-223">
        <h2 className="text-3xl md:text-4xl font-serif text-amber-400 mb-8" data-id="element-224">
          Aquí está lo que nadie dice en voz alta:
        </h2>
        <p className="text-2xl font-serif italic text-amber-200 mb-8" data-id="element-225">
          El buscador espiritual moderno está agotado.
        </p>
        <p data-id="element-226">
          Has hecho el trabajo. Has hecho más que el trabajo. Has dado años — a
          veces décadas — a la búsqueda. Y hay una soledad particular en ser
          alguien que ya no puede fingir que la superficie es suficiente, pero
          que todavía no ha encontrado la profundidad que sabe que existe.
        </p>
        <p data-id="element-227">
          Esa soledad no se anuncia. Se esconde dentro de la productividad,
          dentro de la curiosidad, dentro del próximo libro, el próximo retiro,
          el próximo maestro. Pero debajo de todo eso hay un dolor silencioso.
        </p>
        <p className="font-serif text-xl text-center my-6" data-id="element-228">
          El dolor de casi saber.
        </p>
        <p data-id="element-229">
          La sensación de que algo enorme está justo más allá de la membrana de
          la percepción ordinaria — y que la membrana es más delgada de lo que
          nadie admite.
        </p>
        <div className="pl-6 border-l-2 border-amber-500/30 my-8 space-y-4 italic text-gray-400" data-id="element-230">
          <p data-id="element-231">La religión organizada te dijo que creyeras sin experimentar.</p>
          <p data-id="element-232">La ciencia convencional te dijo que dudaras sin sentir.</p>
          <p data-id="element-233">La autoayuda te dijo que optimizaras sin trascender.</p>
        </div>
        <p className="text-xl font-serif text-amber-400 text-center" data-id="element-234">
          Ninguna te dio permiso para ir hasta el fondo.
        </p>
      </TextSection>

      <Divider data-id="element-235" />

      <TextSection data-id="element-236">
        <h2 className="text-3xl md:text-4xl font-serif text-amber-400 mb-8" data-id="element-237">
          Imagina leer algo que no te pide que creas nada.
        </h2>
        <p data-id="element-238">
          Que simplemente te muestra — en un lenguaje que se mueve como música y
          piensa como ciencia — qué sucede cuando el ego deja de controlar el
          espectáculo.
        </p>
        <p data-id="element-239">
          Imagina entender, por primera vez, por qué cada tradición mística de
          cada cultura describe la misma experiencia fundamental: la disolución
          de la separación. El reconocimiento de que lo que llamas "yo" es un
          patrón hermoso y temporal dentro de un campo infinito de conciencia.
        </p>
        <p className="font-serif text-xl text-amber-200" data-id="element-240">
          Imagina no solo saber esto intelectualmente — sino sentirlo codificado
          en las palabras mismas.
        </p>
        <p className="mt-8" data-id="element-241">
          Cerrarías el libro transformado. No porque te dijera qué pensar. Sino
          porque te reflejó lo que ya sospechabas:
        </p>
        <p className="text-2xl md:text-3xl font-serif text-center text-amber-400 leading-tight my-12" data-id="element-242">
          Que no eres una cosa pequeña dentro de un universo grande. Eres el
          universo, brevemente, hermosamente, despierto dentro de una forma.
        </p>
      </TextSection>

      <Divider data-id="element-243" />

      <TextSection data-id="element-244">
        <h2 className="text-3xl md:text-4xl font-serif text-amber-400 mb-8" data-id="element-245">
          Aquí está lo que cambia todo.
        </h2>
        <p className="text-xl font-serif text-amber-200 mb-6" data-id="element-246">
          El DMT no es ajeno a tu cuerpo. Es de tu cuerpo.
        </p>
        <p data-id="element-247">
          Ha sido encontrado en la sangre humana, en los pulmones, en el fluido
          cerebral. Detectado en los cerebros vivos de mamíferos en 2019. Actúa
          sobre los mismos receptores de serotonina que regulan tu sentido del
          yo — y cuando lo hace, algo extraordinario ocurre.
        </p>
        <p data-id="element-248">
          La red del cerebro que mantiene tu narrativa — la arquitectura del
          "soy esta persona, en esta vida, con esta historia" — se silencia.
          Significativamente. Mediblemente.
        </p>
        <p className="font-serif text-2xl text-center my-8 italic" data-id="element-249">
          Y en ese silencio, algo más habla.
        </p>
        <p data-id="element-250">
          No una alucinación. No un mal funcionamiento. Algo que miles de
          personas a través de miles de años y miles de culturas describen en
          términos casi idénticos:
        </p>
        <p className="text-2xl font-serif text-amber-400 text-center my-8" data-id="element-251">
          La experiencia de ser el todo.
        </p>
        <p className="text-gray-400 italic text-center mb-8" data-id="element-252">
          Los chamanes ancestrales lo llamaron el espíritu de la planta. Los
          textos védicos lo llamaron Brahman. Los físicos lo llaman el campo
          unificado. Los místicos lo llaman Dios.
        </p>
        <p data-id="element-253">
          La molécula no crea esta experiencia. La molécula revela que la
          barrera entre tú y esta experiencia nunca fue sólida.
        </p>
        <p className="font-serif text-xl text-amber-200 mt-6" data-id="element-254">
          Ese es el mecanismo. Esa es la llave.
        </p>
        <p data-id="element-255">
          La Llave Interior fue puesta dentro de ti al nacer. Ha estado allí
          cada noche en tus sueños, quizás cada momento en el borde del sueño,
          potencialmente en tus momentos finales de vida.
        </p>
        <p className="text-center font-serif text-xl mt-8" data-id="element-256">
          Este libro es el primero que mapea esa llave con las manos de un
          científico y un poeta al mismo tiempo.
        </p>
      </TextSection>

      <MidCTA headline="La llave ya estaba dentro de ti. Este libro la nombra." subline="Comienza el viaje hoy — al precio más bajo que tendrá." data-id="element-257" />

      <Guarantee data-id="element-258" />

      <Divider data-id="element-259" />

      <TextSection data-id="element-260">
        <h2 className="text-3xl md:text-4xl font-serif text-amber-400 mb-8" data-id="element-261">
          Este libro no comenzó en un laboratorio ni en una editorial.
        </h2>
        <p data-id="element-262">Comenzó en una conversación. En una sola frase:</p>
        <p className="text-2xl md:text-3xl font-serif text-center text-amber-200 italic my-10" data-id="element-263">
          "Hay una sustancia que te muestra el origen del universo."
        </p>
        <p data-id="element-264">
          Dicha en voz baja. Dicha con calma. Dicha por alguien que había estado
          allí.
        </p>
        <p data-id="element-265">
          Esa frase quedó suspendida en el aire durante días — no porque fuera
          dramática, sino porque fue entregada con la quietud particular de
          alguien que había dejado de necesitar convencer.
        </p>
        <p data-id="element-266">
          Los autores pasaron años después de ese momento. Leyendo a Rick
          Strassman. Estudiando los textos védicos y las tradiciones chamánicas.
          Hablando con investigadores y curanderos. Viviendo sus propios
          encuentros con el borde de la conciencia.
        </p>
        <p className="font-serif text-xl text-amber-400 mt-8" data-id="element-267">
          Este libro es lo que necesitaban que existiera cuando estaban
          buscando.
        </p>
        <p className="text-center italic mt-6" data-id="element-268">
          Está escrito para la persona que ya terminó de buscar permiso para ir
          más profundo.
        </p>
      </TextSection>

      <ChapterList data-id="element-269" />

      <MidCTA headline="Diez capítulos. Un solo umbral." subline="Cruza la membrana. $27 USD — 75% de descuento por tiempo limitado." data-id="element-270" />

      <Divider data-id="element-271" />

      <TextSection data-id="element-272">
        <h2 className="text-3xl md:text-4xl font-serif text-amber-400 mb-8 text-center" data-id="element-273">
          Lo que este libro hace por ti no se mide en información.
        </h2>
        <p className="text-2xl font-serif text-center italic text-amber-200 mb-12" data-id="element-274">
          Se mide en lo que cambia después de cerrarlo.
        </p>
        <ul className="space-y-6 list-none pl-0" data-id="element-275">
          <li className="flex items-start gap-4" data-id="element-276">
            <span className="text-amber-500 mt-1" data-id="element-277">✦</span>
            <span data-id="element-278">
              Dejas de preguntarte si lo que sentiste fue real. Entiendes por
              qué la experiencia es real — no un mal funcionamiento, sino una
              revelación que tu propio cuerpo estaba diseñado para tener.
            </span>
          </li>
          <li className="flex items-start gap-4" data-id="element-279">
            <span className="text-amber-500 mt-1" data-id="element-280">✦</span>
            <span data-id="element-281">
              Obtienes las palabras para articular lo que has sentido pero nunca
              has podido decir — a ti mismo y a los demás.
            </span>
          </li>
          <li className="flex items-start gap-4" data-id="element-282">
            <span className="text-amber-500 mt-1" data-id="element-283">✦</span>
            <span data-id="element-284">
              La experiencia no se evapora. Aprendes a vivirla, no solo a
              recordarla.
            </span>
          </li>
          <li className="flex items-start gap-4" data-id="element-285">
            <span className="text-amber-500 mt-1" data-id="element-286">✦</span>
            <span data-id="element-287">
              Descubres que no estás solo — que cada tradición en toda la
              historia humana ha estado mapeando el mismo territorio desde
              distintas orillas.
            </span>
          </li>
        </ul>
        <p className="mt-10 mb-6" data-id="element-288">
          Y algo más sucede, que es difícil de describir antes de que ocurra:
        </p>
        <p className="text-2xl md:text-3xl font-serif text-center text-amber-400" data-id="element-289">
          Dejas de performar la espiritualidad.
          <br data-id="element-290" />
          <span className="italic text-amber-200" data-id="element-291">Y empiezas a habitarla.</span>
        </p>
      </TextSection>

      <Divider data-id="element-292" />

      <TextSection data-id="element-293">
        <h2 className="text-3xl md:text-4xl font-serif text-amber-400 mb-10" data-id="element-294">
          La ciencia respalda lo que encuentras en estas páginas.
        </h2>

        <div className="space-y-8" data-id="element-295">
          <div className="bg-cosmic-800/50 p-6 rounded-xl border border-white/5" data-id="element-296">
            <p className="m-0 text-gray-300" data-id="element-297">
              <strong className="text-amber-200 font-serif text-xl block mb-2" data-id="element-298">
                Producción Endógena
              </strong>
              En 2019, investigadores de la Universidad de Michigan detectaron
              DMT en los cerebros vivos de mamíferos — confirmando que el
              cerebro no solo recibe esta molécula, sino que la produce.
            </p>
          </div>

          <div className="bg-cosmic-800/50 p-6 rounded-xl border border-white/5" data-id="element-299">
            <p className="m-0 text-gray-300" data-id="element-300">
              <strong className="text-amber-200 font-serif text-xl block mb-2" data-id="element-301">
                Estudios Clínicos
              </strong>
              El Dr. Rick Strassman administró DMT a voluntarios en condiciones
              clínicas controladas durante años. Los reportes fueron
              consistentes a través de personas que no se conocían, no se
              comunicaban, y venían de culturas completamente distintas:
              disolución del ego, contacto con presencias inteligentes,
              experiencias descritas como más reales que la realidad misma.
            </p>
          </div>

          <div className="bg-cosmic-800/50 p-6 rounded-xl border border-white/5" data-id="element-302">
            <p className="m-0 text-gray-300" data-id="element-303">
              <strong className="text-amber-200 font-serif text-xl block mb-2" data-id="element-304">
                Neuroimagen
              </strong>
              Los estudios de neuroimagen muestran que bajo DMT, la Red por
              Defecto — la arquitectura cerebral del "yo" — se silencia de forma
              medible. El mismo efecto que los meditadores avanzados logran
              después de décadas de práctica.
            </p>
          </div>

          <div className="bg-cosmic-800/50 p-6 rounded-xl border border-white/5" data-id="element-305">
            <p className="m-0 text-gray-300" data-id="element-306">
              <strong className="text-amber-200 font-serif text-xl block mb-2" data-id="element-307">
                Física Cuántica
              </strong>
              David Bohm, uno de los físicos más importantes del siglo XX,
              propuso que la realidad visible es una proyección de un orden
              implicado — un campo de información donde todo está
              interconectado. Lo que los chamanes ven bajo Ayahuasca y lo que
              los físicos describen en ecuaciones apuntan al mismo horizonte.
            </p>
          </div>
        </div>

        <p className="text-2xl font-serif text-center text-amber-400 mt-12 italic" data-id="element-308">
          Los datos no exigen fe. Solo atención.
        </p>
      </TextSection>

      <PullQuote data-id="element-309" />

      <TextSection data-id="element-310">
        <h2 className="text-3xl md:text-4xl font-serif text-amber-400 mb-8 text-center" data-id="element-311">
          No arriesgas nada.
        </h2>
        <p data-id="element-312">
          Si abres este libro y encuentras que habla el idioma que has estado
          buscando — si aunque sea un solo capítulo cambia cómo entiendes tu
          propia mente — entonces ha hecho más de lo que la mayoría de las
          experiencias prometen.
        </p>
        <p data-id="element-313">
          Si llegas ya sabiendo todo lo que hay aquí, al menos habrás encontrado
          la articulación más elegante de ello que jamás hayas leído.
        </p>
        <p className="text-xl font-serif text-center text-amber-200 mt-8" data-id="element-314">
          De cualquier manera, la molécula ya estaba dentro de ti cuando
          empezaste.
        </p>
        <p className="text-center italic mt-4" data-id="element-315">
          Este libro simplemente te recuerda que está ahí.
        </p>
      </TextSection>

      <Divider data-id="element-316" />

      <TextSection data-id="element-317">
        <h2 className="text-3xl md:text-4xl font-serif text-amber-400 mb-8" data-id="element-318">
          Este no es un libro para todo el mundo.
        </h2>
        <p className="text-xl font-serif text-gray-300 mb-6" data-id="element-319">
          Es para la persona que ha llegado al borde de lo que la búsqueda
          convencional puede ofrecer.
        </p>
        <ul className="space-y-4 list-none pl-0 text-gray-400 italic" data-id="element-320">
          <li data-id="element-321">— Para quien sabe que falta algo pero no puede nombrarlo.</li>
          <li data-id="element-322">
            — Para quien ha tenido un destello — una vez, quizás más — de algo
            que no pudo sostener.
          </li>
          <li data-id="element-323">
            — Para quien está listo para ir hasta el fondo sin que nadie le diga
            qué encontrará allí.
          </li>
        </ul>
        <p className="text-2xl font-serif text-amber-400 text-center mt-12" data-id="element-324">
          Esa persona no espera.
        </p>
        <p className="text-xl font-serif text-amber-200 text-center italic mt-4" data-id="element-325">
          Porque ya ha esperado suficiente.
        </p>
      </TextSection>

      <Guarantee variant="compact" data-id="element-326" />

      <FinalCTA data-id="element-327" />

      <Footer data-id="element-328" />

      <StickyCTA data-id="element-329" />
    </main>;
};